/**
 * SortableToolItem - Drag & Drop fähiges Tool-Item
 *
 * Verwendet @dnd-kit für Sortierung via Drag & Drop.
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Star } from 'lucide-react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/v2/ui';
import type { WorkspaceToolWithIcon } from '@/hooks/useNavigationPreferences';

interface SortableToolItemProps {
  tool: WorkspaceToolWithIcon;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  isDraggable?: boolean;
  collapsed?: boolean;
}

export function SortableToolItem({
  tool,
  isFavorite = false,
  onToggleFavorite,
  isDraggable = true,
  collapsed = false,
}: SortableToolItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: tool.id,
      disabled: !isDraggable,
      // Require 5px movement before drag starts (prevents accidental drags)
      // @ts-expect-error - activationConstraint is valid but not in current type definitions
      activationConstraint: {
        distance: 5,
      },
    });

  const navigate = useNavigate();
  const location = useLocation();
  const Icon = tool.icon;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const baseClasses = cn(
    'flex items-center rounded-lg',
    'text-sm font-semibold transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-primary-500',
    collapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'
  );

  const handleNavClick = (e: React.MouseEvent) => {
    // Prevent navigation during drag
    if (isDragging) {
      e.preventDefault();
      return;
    }

    // Reload if already on same page
    if (location.pathname === tool.path) {
      e.preventDefault();
      navigate(tool.path, { replace: true, state: { reload: Date.now() } });
    }
  };

  if (tool.is_disabled) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        title={collapsed ? tool.name : undefined}
        className={cn(baseClasses, 'text-neutral-400 dark:text-neutral-600 cursor-not-allowed')}
      >
        {isDraggable && <DragHandle listeners={listeners} attributes={attributes} />}
        <Icon className="h-5 w-5 flex-shrink-0" />
        {!collapsed && <span className="flex-1 truncate">{tool.name}</span>}
        {!collapsed && tool.badge && (
          <Badge variant="secondary" size="sm">
            {tool.badge}
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      title={collapsed ? tool.name : undefined}
      className={cn('group relative', isDragging && 'z-50 opacity-80')}
    >
      <NavLink
        to={tool.path}
        onClick={handleNavClick}
        className={({ isActive }) =>
          cn(
            baseClasses,
            isActive
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
              : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800'
          )
        }
      >
        {isDraggable && <DragHandle listeners={listeners} attributes={attributes} />}
        <Icon className="h-5 w-5 flex-shrink-0" />
        {!collapsed && <span className="flex-1 truncate">{tool.name}</span>}
        {/* Show static star only when there's no toggle button (sidebar only) */}
        {!collapsed && isFavorite && !onToggleFavorite && (
          <Star className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden="true" />
        )}
        {!collapsed && tool.badge && (
          <Badge variant="secondary" size="sm">
            {tool.badge}
          </Badge>
        )}
      </NavLink>

      {/* Quick Favorite Toggle (on hover) */}
      {!collapsed && onToggleFavorite && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleFavorite();
          }}
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2',
            'p-1.5 rounded-md',
            'opacity-0 group-hover:opacity-100',
            'hover:bg-neutral-200 dark:hover:bg-neutral-700',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:opacity-100',
            'transition-opacity'
          )}
          title={isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
          aria-label={isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
        >
          <Star
            className={cn(
              'h-4 w-4',
              isFavorite
                ? 'fill-amber-400 text-amber-400'
                : 'text-neutral-400 dark:text-neutral-500'
            )}
          />
        </button>
      )}
    </div>
  );
}

interface DragHandleProps {
  listeners?: ReturnType<typeof useSortable>['listeners'];
  attributes?: ReturnType<typeof useSortable>['attributes'];
}

function DragHandle({ listeners, attributes }: DragHandleProps) {
  return (
    <button
      className={cn(
        'p-0.5 -ml-1 cursor-grab active:cursor-grabbing',
        'text-neutral-400 hover:text-neutral-600',
        'dark:text-neutral-500 dark:hover:text-neutral-400',
        'focus:outline-none'
      )}
      {...listeners}
      {...attributes}
      aria-label="Zum Sortieren ziehen"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
}
